import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';
import {
  DrawerContent,
  DrawerTab,
  isTaskModalTab,
  OpenTaskExecutionDrawerData,
} from 'context/accountDrawer/types';
import { AccountDrawerControllerContext } from './contexts';

const _getNewForceReload = () => Math.random() * 1_000_000;

const AccountDrawerControllerProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [drawerContent, setDrawerContentState] = useState<
    DrawerContent | undefined
  >(undefined);

  // useState setters are referentially stable across renders, so wrapping
  // these in useCallback with only that setter as a dependency keeps their
  // identity stable regardless of how often ancestor providers re-render —
  // downstream consumers (e.g. AccountDrawerProvider's openContactDrawer
  // etc.) depend on these and would otherwise re-create on every unrelated
  // ancestor render, breaking their own memoization.
  const setDrawerContent = useCallback(
    (content: DrawerContent | undefined) => {
      setDrawerContentState(content);
    },
    [setDrawerContentState],
  );

  const openTaskExecutionDrawer = useCallback(
    ({
      accountId,
      prospectId,
      task,
      tab,
      callOnSuccess,
      defaultOpenTemplateTab,
      forceReload,
    }: OpenTaskExecutionDrawerData) => {
      setDrawerContentState((prev) => {
        // Walking the flow (Previous/Next, or advancing once a task is
        // completed) swaps the task inside the split panel instead of
        // collapsing it: the side drawer keeps the non-modal tab it was
        // already showing, which is the one the user chose when splitting.
        const keepSplitView = !!prev?.isTaskSplitView && isTaskModalTab(tab);

        return {
          tab: keepSplitView ? (prev?.tab ?? 'account') : tab,
          modalVisible: tab,
          accountId,
          selectedProspectId: prospectId ?? undefined,
          task,
          defaultOpenTemplateTab,
          callOnSuccess,
          ...(keepSplitView ? { isTaskSplitView: true } : {}),
          ...(forceReload ? { forceReload: _getNewForceReload() } : {}),
        };
      });
    },
    [setDrawerContentState],
  );

  const closeDrawer = useCallback(() => {
    setDrawerContentState(undefined);
  }, [setDrawerContentState]);

  const removeModal = useCallback(
    (returnTab?: DrawerTab) => {
      setDrawerContentState((prev) =>
        prev
          ? {
              ...prev,
              modalVisible: undefined,
              isTaskSplitView: false,
              ...(returnTab ? { tab: returnTab } : {}),
            }
          : undefined,
      );
    },
    [setDrawerContentState],
  );

  return (
    <AccountDrawerControllerContext.Provider
      value={{
        drawerContent: drawerContent ?? null,
        setDrawerContent,
        openTaskExecutionDrawer,
        closeDrawer,
        removeModal,
      }}
    >
      {children}
    </AccountDrawerControllerContext.Provider>
  );
};

export function useAccountDrawerController() {
  const context = useContext(AccountDrawerControllerContext);
  if (context === undefined) {
    throw new Error(
      'useAccountDrawerController must be used within a AccountDrawerControllerProvider',
    );
  }
  return context;
}

export default AccountDrawerControllerProvider;
